import { Component } from 'react'
import * as debounce from 'lodash/debounce'
import { OverlayContainer } from 'components/features/pfm/Overlay/OverlayContainer'
import YoutubeAPILoader from 'components/atoms/YoutubeAPILoader'
import YoutubePlayer from 'components/atoms/YoutubePlayer'
// import PlayButton from 'components/atoms/buttons/PlayButton'
import CloseButton from 'components/atoms/buttons/CloseButton'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { Document } from 'interfaces/DOM'
import { FULLSCREEN_CHANGE_EVENTS } from 'constants/events'
import * as styles from 'components/features/pfm/HomePage/HowitworksVideo.styl'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'

const HOWITWORKS_VIDEO_ID = 'mWFlIYZtRD4'
const PLAYER_ID = 'howitworks-video'
const ASPECT_RATIO = 16 / 9
const LARGE_SCREEN_WIDTH = 1024
const VIDEO_MIN_WIDTH = 854
const PLAYER_VARS = {
  autoplay: 1,
  showinfo: 0,
  rel: 0,
}

declare const document: Document

type HowitworksVideoState = {
  shouldShowVideo: boolean
  playerWidth: number
  playerHeight: number
}

const HOWITWORKS_VIDEO_OPEN_CLASS = 'howitworksVideoOpen'

class HowitworksVideo extends Component<{}, HowitworksVideoState> {
  constructor (props: {}, context: any) {
    super(props, context)

    this.state = {
      shouldShowVideo: false,
      ...this.getPlayerSize(),
    }
  }

  render () {
    const { children } = this.props

    return (
      <section className={ styles.howitworksVideo }>
        <AnalyticsClick category='page_controls-homepage' action='click-video_cta'>
          <div onClick={ this.handlePlayButtonClick }>{ children }</div>
        </AnalyticsClick>
        { this.state.shouldShowVideo && this.renderVideo() }
      </section>
    )
  }

  renderVideo () {
    const closeButton = (
      <div className={ styles.closeButton }>
        <CloseButton
          theme='light'
          onClick={ this.closeVideo }
        />
      </div>
    )

    return (
      <YoutubeAPILoader
        render={ ({ isReady }) =>
          isReady ? (
            <OverlayContainer onClose={ this.closeVideo }>
              <div className={ styles.modalWrapper }>
                { closeButton }
                <YoutubePlayer
                  videoId={ HOWITWORKS_VIDEO_ID }
                  playerId={ PLAYER_ID }
                  playerVars={ PLAYER_VARS }
                  width={ this.state.playerWidth }
                  height={ this.state.playerHeight }
                />
              </div>
            </OverlayContainer>
          ) : <Spinner size='large' /> }
      />
    )
  }

  componentDidMount () {
    window.addEventListener('resize', this.handleResizeDebounced)

    FULLSCREEN_CHANGE_EVENTS.forEach((eventName: string) => {
      document.addEventListener(eventName, this.handleChangeFullscreen, true)
    })
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResizeDebounced)

    FULLSCREEN_CHANGE_EVENTS.forEach((eventName: string) => {
      document.removeEventListener(eventName, this.handleChangeFullscreen, true)
    })

    this.handleResizeDebounced.cancel()
  }

  private returnFocus () {
    const bodyTabIndex = document.body.tabIndex
    document.body.tabIndex = 0
    document.body.focus()
    document.body.tabIndex = bodyTabIndex
  }

  private getPlayerSize: () => { playerWidth: number, playerHeight: number } = () => {
    const width = window.innerWidth < LARGE_SCREEN_WIDTH
      ? Math.min(VIDEO_MIN_WIDTH, window.innerWidth)
      : Math.max(VIDEO_MIN_WIDTH, window.innerWidth * 0.6)

    const height = Math.min(width / ASPECT_RATIO, window.innerHeight)

    return {
      playerWidth: width,
      playerHeight: height,
    }
  }

  private setPreventPageScroll = (isOpen: boolean) => {
    if (isOpen) {
      document.documentElement.classList.add(HOWITWORKS_VIDEO_OPEN_CLASS)
    } else {
      document.documentElement.classList.remove(HOWITWORKS_VIDEO_OPEN_CLASS)
    }
  }

  private closeVideo = () => {
    this.setState({ shouldShowVideo: false }, () => this.setPreventPageScroll(false))
  }

  private handleResizeDebounced = debounce(() => {
    this.setState({
      ...this.getPlayerSize(),
    })
  }, 50)

  private handleChangeFullscreen = () => {
    if (!document.activeElement || document.activeElement.id !== PLAYER_ID) {
      return
    }

    // Exit fullscreen
    if (!(document.fullscreenElement || document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement)) {
      // get focus back to our app
      this.returnFocus()
    }
  }

  private handlePlayButtonClick = () => {
    this.setState({ shouldShowVideo: true }, () => this.setPreventPageScroll(true))
  }
}

export default HowitworksVideo
